import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalParagraph,
    ModalTitle
} from '@components/ui/modal-atoms'
import i18n, {useTranslation, TKey} from '@/translations/i18n.tsx'
import {
    ResponseUnresponsiveHostReasons,
    useUnresponsiveHostReasons
} from '@features/assignment/queries/useUnresponsiveHostReasons'
import {Spinner} from '@components/ui/spinner'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon} from '@components/ui/icon'
import {Controller, useForm, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {zodResolver} from '@hookform/resolvers/zod'
import {useAssignmentCoordination} from '@features/assignment/queries/useAssignmentCoordination'
import {Button} from '@components/ui/button'
import {Radio, RadioGroup} from '@components/commons/radio'
import {TextArea} from '@components/commons/textarea'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {noUnsupportedChars} from '@utilities/helpers'

const OTHER_REASON_ID = 4
const TEXTAREA_MAX_LENGTH = 2000

const unresponsiveHostReasonToLabel = {
    host_has_not_responded: 'assignment:descriptions:unresponsive_host_modal:has_not_responded_reason',
    host_has_stopped_responding: 'assignment:descriptions:unresponsive_host_modal:has_stopped_responding_reason',
    invalid_cellular_number_provided:
        'assignment:descriptions:unresponsive_host_modal:invalid_cellular_number_provided_reason',
    other: 'commons:other'
} as const satisfies Record<ResponseUnresponsiveHostReasons[number]['name'], TKey>

const ValidationSchema = z
    .object({
        reason_id: z.coerce.number().positive().int(),
        notes: noUnsupportedChars(z.string().max(TEXTAREA_MAX_LENGTH).optional())
    })
    .superRefine((values, context) => {
        if (values.reason_id == OTHER_REASON_ID && !values.notes) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['notes']
            })
        }
    })
type ValidationSchema = z.infer<typeof ValidationSchema>

export const UnresponsiveHostModal = ({
    assignmentId,
    taskId,
    onClose
}: {
    assignmentId: number
    taskId: number
    onClose: () => void
}) => {
    const {t} = useTranslation()
    const unresponsiveHostReasonsQuery = useUnresponsiveHostReasons()
    const hostUnresponsiveMutation = useAssignmentCoordination({assignmentId, taskId, options: {onSuccess: onClose}})
    const form = useForm<ValidationSchema>({mode: 'onChange', resolver: zodResolver(ValidationSchema)})
    const selectedReason = useWatch({name: 'reason_id', control: form.control})
    const handleSubmit = form.handleSubmit(values => {
        hostUnresponsiveMutation.mutate({
            status: 'host_is_unresponsive',
            reason_id: values.reason_id,
            notes: values.reason_id == OTHER_REASON_ID ? values.notes : undefined
        })
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-120" showCloseButton={false}>
                <ModalHeader>
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('assignment:descriptions:unresponsive_host_modal:title')}</ModalTitle>
                        <ModalParagraph>{t('assignment:descriptions:unresponsive_host_modal:subtitle')}</ModalParagraph>
                    </div>
                    {unresponsiveHostReasonsQuery.isPending ? (
                        <Spinner size={20} />
                    ) : unresponsiveHostReasonsQuery.isError ? (
                        <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
                    ) : (
                        <div className="flex flex-col gap-4">
                            <Controller
                                control={form.control}
                                name="reason_id"
                                render={({field: {onChange, value}}) => (
                                    <RadioGroup
                                        onValueChange={(value: string) => {
                                            onChange(value)
                                            form.resetField('notes')
                                            form.clearErrors('notes')
                                        }}
                                        value={value !== undefined ? value : ''}
                                        className="flex flex-col gap-4"
                                    >
                                        {unresponsiveHostReasonsQuery.data.map(reason => (
                                            <Radio
                                                value={reason.id}
                                                label={t(unresponsiveHostReasonToLabel[reason.name])}
                                                id={reason.id.toString()}
                                                key={reason.id}
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            />

                            {selectedReason == OTHER_REASON_ID && (
                                <TextArea
                                    {...form.register('notes')}
                                    maxLength={TEXTAREA_MAX_LENGTH}
                                    rows={4}
                                    placeholder={t('assignment:descriptions:unresponsive_host_modal:notes_placeholder')}
                                    helpText={
                                        <p style={{textAlign: 'right'}}>
                                            {t('commons:x_characters_available', {
                                                count: TEXTAREA_MAX_LENGTH - (form.watch('notes')?.length ?? 0)
                                            })}
                                        </p>
                                    }
                                    errorMessage={form.formState.errors.notes?.message}
                                />
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="tertiary" disabled={hostUnresponsiveMutation.isPending}>
                        {t('commons:close')}
                    </Button>
                    <Button
                        disabled={hostUnresponsiveMutation.isPending || !form.formState.isValid}
                        onClick={handleSubmit}
                    >
                        {t('commons:confirm')}
                        {hostUnresponsiveMutation.isPending && <Spinner size={20} />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
